import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    startChatConnection, stopChatConnection, getChatPageOpen, onChatPageOpenChanged,
} from './chatSocket';

const isTabActive = () => document.visibilityState === 'visible' && document.hasFocus();

/**
 * Mount this ONCE, at DashboardLayout (or an equally top-level app shell) —
 * never inside the chat page itself. The hub connection is only kept up
 * while the user is signed in, on the Chat page (see ChatPage's
 * setChatPageOpen calls), AND the browser tab is visible/focused — presence
 * on the backend is tied directly to this connection (see chatSocket.js's
 * module comment), so this is what makes a user show "offline" as soon as
 * they leave chat or switch away, rather than staying "online" for the rest
 * of the session.
 */
export default function useChatSocket() {
    const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
    const [chatPageOpen, setChatPageOpenState] = useState(getChatPageOpen());
    const [tabActive, setTabActive] = useState(isTabActive());

    useEffect(() => onChatPageOpenChanged(setChatPageOpenState), []);

    useEffect(() => {
        const onChange = () => setTabActive(isTabActive());
        document.addEventListener('visibilitychange', onChange);
        window.addEventListener('focus', onChange);
        window.addEventListener('blur', onChange);
        return () => {
            document.removeEventListener('visibilitychange', onChange);
            window.removeEventListener('focus', onChange);
            window.removeEventListener('blur', onChange);
        };
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !chatPageOpen || !tabActive) {
            stopChatConnection();
            return undefined;
        }
        startChatConnection();
        return undefined;
    }, [isAuthenticated, chatPageOpen, tabActive]);

    useEffect(() => () => stopChatConnection(), []);
}
